import { Helmet } from 'react-helmet-async';

/**
 * SEO Component — Luxury Wood Concept
 * Schema.org enrichi : Organization, LocalBusiness, BreadcrumbList, FAQ, Product
 */
export default function SEO({ 
  title, 
  description, 
  path = '', 
  type = 'website', 
  image = '/og-image.png',
  schema = null,
  breadcrumbs = null,
  faq = null,
  keywords = [],
  noindex = false,
}) {
  const siteUrl = 'https://luxurywoodconcept.ma';
  const fullUrl = `${siteUrl}${path}`;
  
  const metaDescription = description || 'Luxury Wood Concept crée des espaces de vie exceptionnels à Rabat, Casablanca et Marrakech. Cuisines, portes, dressings et aménagements de villas sur mesure. Devis gratuit.';

  // ── Organization Schema (parent) ──
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Luxury Wood Concept",
    "url": siteUrl,
    "logo": `${siteUrl}/logo-luxury-wood-concept-web.png`,
    "image": `${siteUrl}${image}`,
    "telephone": "+212661338406",
    "email": "contact@luxurywoodconcept.ma",
    "foundingDate": "2010",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "minValue": 10,
      "maxValue": 50
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+212661338406",
        "contactType": "customer service",
        "availableLanguage": ["French"],
        "areaServed": "MA"
      },
      {
        "@type": "ContactPoint",
        "telephone": "+212661338406",
        "contactType": "sales",
        "availableLanguage": ["French"],
        "areaServed": "MA",
        "url": "https://wa.me/212661338406"
      }
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "18 Lot Zerdal, Bouknadel",
      "addressLocality": "Salé",
      "addressRegion": "Rabat-Salé-Kénitra",
      "postalCode": "11000",
      "addressCountry": "MA"
    },
    "areaServed": [
      { "@type": "City", "name": "Rabat" },
      { "@type": "City", "name": "Salé" },
      { "@type": "City", "name": "Casablanca" },
      { "@type": "City", "name": "Marrakech" },
      { "@type": "City", "name": "Tanger" },
      { "@type": "City", "name": "Kénitra" },
      { "@type": "City", "name": "Agadir" },
      { "@type": "Country", "name": "Morocco" }
    ],
    "sameAs": [
      "https://www.instagram.com/abdelali224?igsh=NTZhenN0MzVlZ3hk",
      "https://www.tiktok.com/@abdelalialam6?_r=1&_t=ZS-95issM3dVK9"
    ],
    "knowsAbout": [
      "Menuiserie sur mesure",
      "Cuisines équipées",
      "Portes en bois",
      "Dressings",
      "Aménagement intérieur",
      "Parquet bois"
    ]
  };

  // ── LocalBusiness Schema (unique address + service areas) ──
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${siteUrl}/#localbusiness`,
    "name": "Luxury Wood Concept",
    "image": `${siteUrl}${image}`,
    "url": siteUrl,
    "telephone": "+212661338406",
    "priceRange": "$$$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "18 Lot Zerdal, Bouknadel",
      "addressLocality": "Salé",
      "addressRegion": "Rabat-Salé-Kénitra",
      "postalCode": "11000",
      "addressCountry": "MA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 34.1081,
      "longitude": -6.7401
    },
    "areaServed": [
      { "@type": "City", "name": "Rabat" },
      { "@type": "City", "name": "Salé" },
      { "@type": "City", "name": "Casablanca" },
      { "@type": "City", "name": "Marrakech" },
      { "@type": "City", "name": "Tanger" },
      { "@type": "Country", "name": "Morocco" }
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "08:00",
        "closes": "19:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services de Menuiserie",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Cuisines Sur Mesure" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Portes Intérieures et d'Entrée" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Dressings et Placards" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Aménagement de Villas" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Aménagement de Bureaux" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Parquet Bois" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Salon Marocain" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Escaliers Bois" } }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "47",
      "bestRating": "5"
    },
    "sameAs": [
      "https://www.instagram.com/abdelali224?igsh=NTZhenN0MzVlZ3hk",
      "https://www.tiktok.com/@abdelalialam6?_r=1&_t=ZS-95issM3dVK9"
    ]
  };

  // ── BreadcrumbList Schema ──
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${siteUrl}${crumb.path}`
    }))
  } : null;

  // ── FAQPage Schema ──
  const faqSchema = faq && faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  } : null;

  // Combine keywords
  const allKeywords = [
    'menuiserie sur mesure maroc',
    'luxury wood concept',
    'menuisier rabat',
    'cuisine sur mesure',
    ...keywords
  ].join(', ');

  return (
    <Helmet>
      {/* Basic Meta */}
      <html lang="fr" dir="ltr" />
      <title>{title}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={fullUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Hreflang - Force French Only */}
      <link rel="alternate" hrefLang="fr" href={`${siteUrl}${path}`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}${path}`} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={`${siteUrl}${image}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="fr_MA" />
      <meta property="og:site_name" content="Luxury Wood Concept" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={`${siteUrl}${image}`} />

      {/* Geo */}
      <meta name="geo.region" content="MA" />
      <meta name="geo.placename" content="Salé, Morocco" />
      <meta name="geo.position" content="34.1081;-6.7401" />
      <meta name="ICBM" content="34.1081, -6.7401" />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}
